/**
 * Form History Utility
 * Stores form submissions locally with TTL and easy restore.
 * Works with any entity type (contacts, tickets, etc.)
 */

export interface FormHistoryItem {
  id: string; // unique id for the history entry
  entityName: string; // e.g., "contacts", "tickets"
  formData: Record<string, unknown>; // all form field values
  timestamp: number; // ms epoch
  preview?: string; // optional preview text (e.g., "Subject: My Task")
}

const STORAGE_PREFIX = "form_history_";
const DEFAULT_MAX = 10;
const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function getStorageKey(entityName: string): string {
  return `${STORAGE_PREFIX}${entityName}`;
}

function loadAllRaw(entityName: string): FormHistoryItem[] {
  if (!isBrowser()) return [];
  try {
    const key = getStorageKey(entityName);
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as FormHistoryItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveAll(entityName: string, items: FormHistoryItem[]): void {
  if (!isBrowser()) return;
  try {
    const key = getStorageKey(entityName);
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // no-op
  }
}

export function getFormHistory(entityName: string, limit: number = DEFAULT_MAX): FormHistoryItem[] {
  const items = loadAllRaw(entityName);
  const cutoff = Date.now() - DEFAULT_TTL_MS;
  const filtered = items.filter(
    (i) => i && typeof i.id === "string" && i.entityName === entityName && i.timestamp >= cutoff
  );

  // If filtering changed length, persist cleanup
  if (filtered.length !== items.length) saveAll(entityName, filtered);

  return filtered.sort((a, b) => b.timestamp - a.timestamp).slice(0, Math.max(1, limit));
}

export type FormHistoryInput = Omit<FormHistoryItem, "id" | "timestamp"> & {
  id?: string;
};

export function addFormHistory(entry: FormHistoryInput): void {
  if (!isBrowser()) return;
  const items = loadAllRaw(entry.entityName);

  const id = entry.id ?? createHistoryId();
  const newItem: FormHistoryItem = {
    ...entry,
    id,
    timestamp: Date.now(),
  };

  // Overwrite by id when provided; otherwise de-dupe by form data signature
  const signature = (i: FormHistoryItem) => JSON.stringify(i.formData).substring(0, 100); // Use first 100 chars of stringified data
  const filtered = items.filter((i) =>
    entry.id ? i.id !== id : signature(i) !== signature(newItem)
  );
  const updated = [newItem, ...filtered].slice(0, DEFAULT_MAX);
  saveAll(entry.entityName, updated);
}

export function deleteFormHistory(entityName: string, id: string): void {
  if (!isBrowser()) return;
  const items = loadAllRaw(entityName);
  const updated = items.filter((i) => i.id !== id);
  saveAll(entityName, updated);
}

export function clearFormHistory(entityName: string): void {
  if (!isBrowser()) return;
  try {
    const key = getStorageKey(entityName);
    window.localStorage.removeItem(key);
  } catch {
    // no-op
  }
}

/**
 * Generate a canonical history id (UUID v4 when available)
 */
export function createHistoryId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  const rand = Math.random().toString(36).slice(2, 10);
  const ts = Date.now().toString(36);
  return `${ts}-${rand}`;
}
